// Map multi-line body — one line per top-N map key, tracing each key's
// numeric value over time. Numeric-only (only in the `map_numeric`
// cycle). The window's top keys are picked by total sum so a chart of
// "expenses by category" focuses on the categories that move the
// budget, not the ones with one $1 row.

import React, { useEffect, useMemo, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { api } from '../../api/client';
import type { ChartBodyProps } from '../types';
import { useTimeRangeState, windowForAnchor, withinWindow } from '../windowRange';
import ChartTimeRangePicker from '../ChartTimeRangePicker';
import { type MapValueRow } from './mapAggregation';
import { assignSliceColors } from './chartColors';
import { numericExtent, numericTickFormatter, numericYAxisWidth } from '../axisWidth';

const TOP_N_LINES = 6;

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatTick(ts: number): string {
  const d = new Date(ts);
  return `${MONTH_SHORT[d.getMonth()]} ${d.getDate()}`;
}

const MapLinesBody: React.FC<ChartBodyProps> = ({ featureId, unit, fullscreen }) => {
  const [rows, setRows] = useState<MapValueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get<MapValueRow[]>(`/api/features/values/${featureId}`)
      .then((resp) => { if (!cancelled) { setRows(resp.data); setError(null); } })
      .catch((err) => {
        console.error('Failed to fetch feature values:', err);
        if (!cancelled) setError('Failed to load chart data');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [featureId]);

  // Restrict to numeric map rows (have key_label + decimal_value) for
  // both the time-range data set and the chart itself. Non-numeric
  // values can't trace a line.
  const numericRows = useMemo(
    () => rows.filter((r) => r.key_label != null && r.decimal_value != null),
    [rows],
  );
  const dataDates = useMemo(
    () => numericRows.map((r) => new Date(r.date).getTime()),
    [numericRows],
  );
  const range = useTimeRangeState(dataDates);
  const win = useMemo(
    () => windowForAnchor(range.granularity, range.anchor),
    [range.granularity, range.anchor],
  );

  const { chartData, lines } = useMemo(() => {
    const inWindow = numericRows.filter((r) => withinWindow(new Date(r.date).getTime(), win));

    const totals = new Map<string, number>();
    for (const r of inWindow) {
      totals.set(r.key_label!, (totals.get(r.key_label!) ?? 0) + (r.decimal_value ?? 0));
    }
    const topKeys = [...totals.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, TOP_N_LINES)
      .map(([k]) => k);
    const topSet = new Set(topKeys);

    // Bucket rows by exact checkin-date timestamp. Within a single key on
    // a single date there'd be at most one pair (uniqueness enforced
    // app-side); sum just in case of multi-checkin-per-day with the same
    // key, since merging by day reads better on the chart.
    const byDate = new Map<number, Record<string, number>>();
    for (const r of inWindow) {
      if (!topSet.has(r.key_label!)) continue;
      const ts = new Date(r.date).setHours(0, 0, 0, 0);
      let bucket = byDate.get(ts);
      if (!bucket) { bucket = {}; byDate.set(ts, bucket); }
      const k = r.key_label!;
      bucket[k] = (bucket[k] ?? 0) + (r.decimal_value ?? 0);
    }

    const sortedDates = [...byDate.keys()].sort((a, b) => a - b);
    const data = sortedDates.map((ts) => ({ date: ts, ...byDate.get(ts)! }));

    const colors = assignSliceColors(topKeys);
    return {
      chartData: data,
      lines: topKeys.map((k, i) => ({ key: k, color: colors[i] })),
    };
  }, [numericRows, win]);

  // Size the Y axis to its widest value label across all series.
  const yAxisWidth = useMemo(() => {
    const vals: number[] = [];
    for (const row of chartData) {
      for (const [k, v] of Object.entries(row)) {
        if (k !== 'date' && typeof v === 'number') vals.push(v);
      }
    }
    const [lo, hi] = numericExtent(vals);
    return numericYAxisWidth(lo, hi, { fontSizePx: 13 });
  }, [chartData]);

  if (loading) {
    return (
      <div className={`flex justify-center items-center ${fullscreen ? 'h-full' : 'h-64'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600" />
      </div>
    );
  }
  if (error) {
    return <div className="text-center py-8 text-rose-500"><p>{error}</p></div>;
  }
  if (numericRows.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No numeric pairs recorded yet for this feature.</p>
      </div>
    );
  }

  return (
    <div className={fullscreen ? 'h-full flex flex-col' : 'flex flex-col'} style={{ touchAction: 'none' }}>
      <ChartTimeRangePicker state={range} />
      <div className={fullscreen ? 'flex-1 min-h-0' : 'h-80'}>
        {chartData.length === 0 ? (
          <div className="flex items-center justify-center h-full text-center text-gray-500">
            <p>No numeric pairs in this period.</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 10, right: fullscreen ? 30 : 20, left: 0, bottom: 25 }}
            >
              <XAxis
                dataKey="date"
                type="number"
                domain={['dataMin', 'dataMax']}
                scale="time"
                tickFormatter={formatTick}
                tick={{ fontSize: 13 }}
                angle={-30}
                textAnchor="end"
                height={fullscreen ? 60 : 45}
                tickLine={false}
              />
              <YAxis tick={{ fontSize: 13 }} width={yAxisWidth} tickFormatter={numericTickFormatter} tickLine={false} />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  const ts = typeof label === 'number' ? label : Number(label);
                  return (
                    <div className="bg-white border border-gray-200 rounded shadow-md px-2 py-1 text-sm">
                      <div className="text-gray-600">{formatTick(ts)}</div>
                      {payload.map((p) => (
                        <div key={String(p.dataKey)} className="text-gray-900">
                          <span style={{ color: p.color }}>■</span>{' '}
                          {String(p.dataKey)}: {unit ? `${p.value} ${unit}` : String(p.value)}
                        </div>
                      ))}
                    </div>
                  );
                }}
              />
              <Legend align="left" wrapperStyle={{ paddingTop: 8 }} />
              {lines.map((l) => (
                <Line
                  key={l.key}
                  type="monotone"
                  dataKey={l.key}
                  stroke={l.color}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  connectNulls
                  isAnimationActive={true}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default MapLinesBody;
